import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { LinkIncident } from '@client/components/incidents/LinkIncident';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { COMPLAINT_STATUS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useCanLinkComplaintIncidents } from '../hooks';
import { complaintFormStore } from '../store';

export const Route = createFileRoute('/console/$orgId/compliance/complaints/details/$id/manage/incidents')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Manage incidents',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplaintIncidentManage />
        </PageController>
    );
}

function ComplaintIncidentManage() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const formData = complaintFormStore.use.formData();
    const { id } = Route.useParams();
    const canLinkIncidents = useCanLinkComplaintIncidents();

    // CUR-296: the overlay has its own URL, so the parent's "Add incident" button gate isn't enough —
    // guard direct navigation with the same permission check, sending the user back to the manage
    // page when linking isn't allowed (no edit access, or a cancelled complaint).
    useEffect(() => {
        if (!canLinkIncidents) {
            navigate({
                to: '/console/$orgId/compliance/complaints/details/$id/manage',
                params: { orgId, id },
                replace: true,
            });
        }
    }, [canLinkIncidents, navigate, orgId, id]);

    if (!canLinkIncidents) {
        return null;
    }

    // CUR-296: a closed complaint may have incidents linked in place, but existing links must not be
    // removable — additive-only locks the already-linked rows in the overlay.
    const additiveOnly = formData.status === COMPLAINT_STATUS.CLOSED;

    return (
        <LinkIncident
            entity="complaint"
            entityId={id}
            urlKey="complaint-incident-manage"
            additiveOnly={additiveOnly}
            rightAddon={
                <Button.Add.Link
                    to="/console/$orgId/compliance/incidents/add"
                    params={{ orgId }}
                    search={{
                        complaint: {
                            complaintId: formData.id,
                            handlingPartyIds: formData.handlingPartyIds,
                            ownerId: formData.ownerId || undefined,
                            products: formData.products,
                            policyNo: formData.policyNo || undefined,
                            externalClaimId: formData.externalClaimId || undefined,
                        },
                    }}
                    variant="primary"
                    label="New incident"
                />
            }
        />
    );
}
